"use client";

import { useEffect } from "react";
import { initializeFaro, getWebInstrumentations } from "@grafana/faro-web-sdk";
import { TracingInstrumentation } from "@grafana/faro-web-tracing";
import { MeterProvider, PeriodicExportingMetricReader } from "@opentelemetry/sdk-metrics";
import { OTLPMetricExporter } from "@opentelemetry/exporter-metrics-otlp-http";
import { onFCP, onLCP, onCLS, onTTFB, onINP } from "web-vitals";

function setupWebVitals(otlpUrl: string, appName: string) {
  const exporter = new OTLPMetricExporter({
    url: `${otlpUrl}/v1/metrics`,
  });

  const provider = new MeterProvider({
    readers: [
      new PeriodicExportingMetricReader({
        exporter,
        exportIntervalMillis: 30000,
      }),
    ],
  });

  const meter = provider.getMeter(appName);
  const attrs = { app: appName };

  const ttfb = meter.createHistogram("web_vital_ttfb", { unit: "ms", description: "Time to First Byte" });
  const fcp  = meter.createHistogram("web_vital_fcp",  { unit: "ms", description: "First Contentful Paint" });
  const lcp  = meter.createHistogram("web_vital_lcp",  { unit: "ms", description: "Largest Contentful Paint" });
  const cls  = meter.createHistogram("web_vital_cls",  { description: "Cumulative Layout Shift (x1000)" });
  const inp  = meter.createHistogram("web_vital_inp",  { unit: "ms", description: "Interaction to Next Paint" });

  onTTFB((m) => ttfb.record(m.value, attrs));
  onFCP((m)  => fcp.record(m.value, attrs));
  onLCP((m)  => lcp.record(m.value, attrs));
  onCLS((m)  => cls.record(m.value * 1000, attrs));
  onINP((m)  => inp.record(m.value, attrs));
}

export function FaroProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    const collectorUrl = process.env.NEXT_PUBLIC_FARO_COLLECTOR_URL;
    const otlpUrl = process.env.NEXT_PUBLIC_OTEL_COLLECTOR_URL;

    if (collectorUrl) {
      initializeFaro({
        url: collectorUrl,
        app: {
          name: "kustomer-app",
          version: process.env.NEXT_PUBLIC_APP_VERSION ?? "1.0.0",
          environment: process.env.NODE_ENV,
        },
        instrumentations: [
          ...getWebInstrumentations({ captureConsole: false }),
          new TracingInstrumentation(),
        ],
      });
    }

    if (otlpUrl) {
      setupWebVitals(otlpUrl, "kustomer-app");
    }
  }, []);

  return <>{children}</>;
}
